import { useRef, useState } from 'react';
import { Link, useLocation } from 'react-router';
import NavItem from '../../components/redesign/NavItem';
import Badge from '../../components/redesign/Badge';
import { useAuth } from '../../contexts/useAuth';
import { isAdmin } from '../../utils/authUtils';
import { isMarketplaceEnabled, isOpportunityFeedEnabled } from '../../config/features';
import { navigationFor } from './navigation';
import { adminGroupsFor, isAdminPath } from './adminNav';
import UserMenu from './UserMenu';
import { hasDaIntelligence, planDisplayName } from '../../utils/entitlements';
import { usePlanPrice } from '../../hooks/usePlanPricing';

const initialsOf = (first?: string, last?: string): string =>
    `${first?.[0] ?? ''}${last?.[0] ?? ''}`.toUpperCase();

interface RedesignSidebarProps {
    /**
     * How many enquiries are still `new`. Fetched once by `RedesignLayout` and handed down here
     * (and to `MobileTabBar`) rather than re-fetched by this component — see the layout's own
     * comment. Omitted or zero renders no badge at all, not a "0".
     */
    enquiriesCount?: number;
    /**
     * Saved DAs this user has not seen on the Saved page yet, from `useNewSavedCount` via the
     * layout. Same zero-renders-nothing rule as the enquiries badge.
     */
    savedCount?: number;
}

/**
 * The prototype's sidebar: fixed 232px, dark green in both themes, plan card pinned to the bottom.
 *
 * Unlike the sidebar it replaces there is no collapse/hover-expand state — the prototype has one
 * width, so the whole SidebarContext dance is simply absent here.
 */
export default function RedesignSidebar({ enquiriesCount, savedCount }: RedesignSidebarProps) {
    const { pathname } = useLocation();
    const { user, activeBusiness } = useAuth();
    const [userMenuOpen, setUserMenuOpen] = useState(false);
    // Wraps the trigger row *and* the menu — the same fix NotificationsPopover/RedesignHeader use
    // for the bell. Without it, a click on the row bubbles a `mousedown` the menu sees as
    // "outside" and closes on, then the row's own click handler re-opens it a beat later, so the
    // row could never close an open menu.
    const userMenuContainerRef = useRef<HTMLDivElement>(null);

    // Which of the sidebar's two navs is showing. Starts drilled in when we have landed on an
    // admin screen, and is derived once rather than synced on every navigation: this is a sidebar
    // mode, not a reflection of the URL. Clicking a management link keeps you in the admin nav
    // (you are on an admin page); Back returns the client nav without navigating anywhere.
    //
    // Gated on isAdmin so a non-admin who somehow reaches an admin URL still gets the client nav
    // rather than a drilled-in sidebar with no way back to it.
    const [adminView, setAdminView] = useState(() => isAdmin(user) && isAdminPath(pathname));

    const entries = navigationFor({
        marketplace: isMarketplaceEnabled(),
        deals: isOpportunityFeedEnabled(),
    });

    // Admin and SuperAdmin both get Management; only SuperAdmin gets Admin. A group with no links
    // is dropped entirely, so an Admin never sees an empty "Admin" heading.
    const showAdminSection = isAdmin(user);
    const adminGroups = adminGroupsFor(user);

    // The plan card's second line. Seats are PEOPLE, and `maxUsers` is the allowance
    // CanAddMember() enforces. The price comes off the live Stripe price rather than a number
    // typed in here — see seatPricing.ts.
    //
    // A PRICE IS ONLY SHOWN TO A BUSINESS THAT IS ACTUALLY BEING BILLED. `maxUsers` defaults to 2
    // and can be set to anything by an admin, so quoting it as a monthly charge told a business on
    // a free trial with an inflated allowance that it owed $1,018.99 a month — a number nobody had
    // agreed to, on an account that had been charged nothing. A trial says it is a trial.
    const seats = Math.max(1, activeBusiness?.maxUsers ?? 1);
    const onIntelligence = hasDaIntelligence(activeBusiness);
    const trialing = activeBusiness?.subscriptionStatus === 'trialing';
    const intelligencePrice = usePlanPrice('intelligence', seats);
    const seatLabel = `${seats} ${seats === 1 ? 'user' : 'users'}`;

    let planSummary: string;
    if (!activeBusiness) {
        planSummary = '—';
    } else if (onIntelligence && trialing) {
        planSummary = `Free trial · ${seatLabel}`;
    } else if (onIntelligence && intelligencePrice) {
        planSummary = `${intelligencePrice}/mo · ${seatLabel}`;
    } else if (onIntelligence) {
        planSummary = seatLabel;
    } else {
        planSummary = activeBusiness.subscriptionStatus ?? 'Free';
    }

    return (
        <aside className="hidden md:flex w-[232px] flex-none bg-sidebar text-white px-[18px] py-6 flex-col gap-1 sticky top-0 h-dvh overflow-auto">
            <div className="px-2.5 pb-5">
                {/* The public home page, as a logo link is expected to be. */}
                <Link to="/" aria-label="DAconnect home" className="block max-w-[176px]">
                    <img src="/images/logo/DA-Connect_logo_title_transparent_dark_logo.svg" alt="" className="w-full max-w-[176px] h-auto block" />
                </Link>
            </div>

            {adminView ? (
                /* Drilled in: the client nav is REPLACED by the admin nav, the way the sidebar
                   this one succeeds behaves. There is no Admin row in here — the back button is
                   the way out, and it changes the view without changing the route. */
                <nav className="flex flex-col gap-1" data-testid="admin-nav">
                    <button
                        type="button"
                        onClick={() => setAdminView(false)}
                        className="flex w-full items-center gap-[11px] rounded-[10px] px-3 py-2.5 text-sm text-sidebar-ink hover:bg-white/10"
                    >
                        <span aria-hidden="true">←</span>
                        <span>Back to app</span>
                    </button>

                    {adminGroups.map((group) => (
                        <div key={group.key} className="flex flex-col gap-1">
                            <h2 className="px-3 pt-2 text-[11px] uppercase tracking-wide text-sidebar-ink">
                                {group.heading}
                            </h2>
                            {group.links.map((link) => (
                                <NavItem
                                    key={link.to}
                                    to={link.to}
                                    label={link.label}
                                    glyph={link.glyph}
                                    indented
                                    active={pathname === link.to}
                                />
                            ))}
                        </div>
                    ))}
                </nav>
            ) : (
                <>
                    <nav className="flex flex-col gap-1">
                        {entries.map((entry) => {
                            // Entries marked `pro` need the Intelligence plan. For a business
                            // without it the row still appears — hiding it would leave no way to
                            // discover the feature — but it is badged and it leads to the plan page
                            // rather than to a screen the server will refuse.
                            const locked = entry.pro === true && !onIntelligence;
                            return (
                                <NavItem
                                    key={entry.key}
                                    to={locked ? '/plan' : entry.to}
                                    label={entry.label}
                                    glyph={entry.glyph}
                                    indented={entry.indented}
                                    active={pathname === entry.to}
                                    badge={
                                        entry.key === 'enquiries' && enquiriesCount
                                            ? <Badge variant="count">{enquiriesCount}</Badge>
                                            // Never on a locked row: the PRO upsell is the point there.
                                            : entry.key === 'saved' && savedCount && !locked
                                                ? <Badge variant="count">{savedCount}</Badge>
                                            : locked
                                                ? <Badge variant="pro">PRO</Badge>
                                                : undefined
                                    }
                                />
                            );
                        })}
                    </nav>

                    {/* The way into the admin screens, pinned above the plan card. A button, not a
                        link: it swaps the nav rather than navigating, so it works from any page and
                        the admin screens keep their own URLs. */}
                    {showAdminSection && (
                        <div className="mt-auto pt-2" data-testid="admin-section">
                            <button
                                type="button"
                                onClick={() => setAdminView(true)}
                                className="flex w-full items-center gap-[11px] rounded-[10px] px-3 py-2.5 text-sm text-sidebar-ink hover:bg-white/10"
                            >
                                <span aria-hidden="true">⚙</span>
                                <span>Admin</span>
                                <span aria-hidden="true" className="ml-auto text-[11px]">›</span>
                            </button>
                        </div>
                    )}
                </>
            )}

            {/* `mt-auto` is what pins the bottom stack to the foot of the aside, and it has to live
                on whichever of these comes first — two auto margins in one flex column split the
                free space between them and leave a gap in the middle. In the admin view there is
                no Admin row above, so the plan card takes the auto margin back and pins itself. */}
            {/* Business settings sits in the sidebar's foot rather than inside the account menu:
                it is a property of the BUSINESS, not of the person signed in, and burying it in a
                menu behind an avatar made it hard to find. The mobile More sheet still carries its
                own entry — there is no sidebar there to put this in. */}
            <Link
                to="/business-details"
                data-testid="business-settings"
                className={`${showAdminSection && !adminView ? 'mt-3' : 'mt-auto'} flex items-center gap-[11px] rounded-[10px] px-3 py-2.5 text-sm text-sidebar-ink hover:bg-white/10`}
            >
                <span aria-hidden="true">⚙</span>
                <span>Business settings</span>
            </Link>

            {/* A real Link, not a div with an onClick: the prototype's card has always LOOKED
                clickable (the "Change ›" affordance) and now goes somewhere, so it needs to be
                reachable by keyboard, middle-clickable, and announced as a link. Its accessible
                name is spelled out because the card's own text is a plan name and a status, which
                on their own say nothing about where the link goes. */}
            <Link
                to="/plan"
                data-testid="plan-card"
                aria-label="Change plan"
                className="mt-2 block bg-white/10 rounded-xl p-3.5 hover:bg-white/15"
            >
                <div className="flex justify-between items-center">
                    <div className="text-xs font-bold text-leaf-pill">
                        {activeBusiness ? planDisplayName(activeBusiness) : 'No active plan'}
                    </div>
                    <span className="text-[11px] text-sidebar-ink">Change ›</span>
                </div>
                <div className="text-[13px] text-sidebar-ink mt-1">{planSummary}</div>
            </Link>

            <div ref={userMenuContainerRef} className="mt-3">
                <button
                    type="button"
                    aria-haspopup="menu"
                    aria-expanded={userMenuOpen}
                    onClick={() => setUserMenuOpen((open) => !open)}
                    className="flex items-center gap-2.5 px-2.5 pt-3.5 pb-0.5 border-t border-white/15 w-full text-left"
                >
                    <span className="size-[34px] rounded-full bg-leaf-pill text-sidebar grid place-items-center text-[13px] font-bold flex-none">
                        {initialsOf(user?.firstName, user?.lastName)}
                    </span>
                    <div className="min-w-0">
                        <div className="text-[13px] font-semibold text-white">
                            {user?.firstName} {user?.lastName}
                        </div>
                        <div className="text-[11.5px] text-sidebar-ink truncate">{activeBusiness?.name}</div>
                    </div>
                    <span className="ml-auto text-sm text-sidebar-ink" aria-hidden="true">⋯</span>
                </button>

                <UserMenu
                    open={userMenuOpen}
                    onClose={() => setUserMenuOpen(false)}
                    boundaryRef={userMenuContainerRef}
                />
            </div>
        </aside>
    );
}
